import { useAxios, useSubjectId } from '@healthblocks-io/core'
import { useCallback } from 'react'
import { useSWRConfig } from 'swr'

import { isDocumentUnread, patchDocumentAsRead } from '../helpers/document-read-status.helpers'

import type {
  PatientDocumentsResponse,
  PatientViewDocument,
  PatientViewDocumentsByYear,
} from '../types/patient-documents.types'

type DocumentsCacheEntry = {
  data: PatientDocumentsResponse['data']
  meta: PatientDocumentsResponse['meta']
}

function hasBrochureAttachment(document: PatientViewDocument) {
  if (document.type === 'Message') {
    return document.attachments?.some((a) => a.attachmentType === 'Brochure')
  }

  if (document.type === 'PatientCommunication') {
    return document.messages?.some((m) => m.attachments?.some((a) => a.attachmentType === 'Brochure')) ?? false
  }

  return false
}

function getPrimaryFilterForDocument(document: PatientViewDocument) {
  if (document.type === 'DocumentFlow') {
    return document.documentFlowStatus === 'signed' ? 'signed' : 'toSign'
  }
  return hasBrochureAttachment(document) ? 'brochures' : 'documents'
}

function patchDocumentsData(
  data: PatientDocumentsResponse['data'],
  documentId: string,
  scope?: 'message' | 'attachment',
): { nextData: PatientDocumentsResponse['data']; found?: PatientViewDocument } {
  const patch = (d: PatientViewDocument) => patchDocumentAsRead(d, scope)

  if (Array.isArray(data)) {
    let found: PatientViewDocument | undefined
    const next = data.map((d) => {
      if (d.id !== documentId) return d
      found = d
      return patch(d)
    })
    return { nextData: next, found }
  }

  const byYear = data as PatientViewDocumentsByYear
  let found: PatientViewDocument | undefined
  const next: PatientViewDocumentsByYear = {}
  Object.entries(byYear).forEach(([year, documents]) => {
    next[Number(year)] = documents.map((d) => {
      if (d.id !== documentId) return d
      found = d
      return patch(d)
    })
  })
  return { nextData: next, found }
}

function decrement(n: number, amount: number) {
  return Math.max(0, n - amount)
}

function patchDocumentsMeta(meta: PatientDocumentsResponse['meta'], document: PatientViewDocument) {
  const primaryFilter = getPrimaryFilterForDocument(document)

  return {
    ...meta,
    openCount: decrement(meta.openCount, 1),
    filters: meta.filters.map((f) => {
      if (f.filter === 'all' || f.filter === primaryFilter) {
        return { ...f, openCount: decrement(f.openCount, 1) }
      }
      return f
    }),
  }
}

export type MarkDocumentAsReadOptions = {
  /** For PatientCommunication: 'message' = conversation read, 'attachment' = document/brochure read. Ignored for other types. */
  scope?: 'message' | 'attachment'
}

export function usePatientDocumentMarkAsRead() {
  const axios = useAxios()
  const { mutate } = useSWRConfig()
  const subjectId = useSubjectId()

  const markAsRead = useCallback(
    async (documentId: string, document?: PatientViewDocument, options?: MarkDocumentAsReadOptions) => {
      const scope = options?.scope
      try {
        if (!subjectId) {
          throw new Error('Subject ID is required')
        }

        // Optimistically update the document lists + unread counts in cache
        await mutate(
          (key) => typeof key === 'string' && key.includes('/documents?subjectId='),
          (current?: DocumentsCacheEntry) => {
            if (!current) return current

            const { nextData, found } = patchDocumentsData(current.data, documentId, scope)
            const basis = found ?? document

            // Decrement only when this action makes the doc "read" in documents list (e.g. attachment scope for PC)
            const wasUnread = basis ? isDocumentUnread(basis, 'documents') : false
            const patched = basis ? patchDocumentAsRead(basis, scope) : null
            const nowRead = patched && !isDocumentUnread(patched, 'documents')
            const shouldDecrement = wasUnread && nowRead

            return {
              ...current,
              data: nextData,
              meta: basis && shouldDecrement ? patchDocumentsMeta(current.meta, basis) : current.meta,
            }
          },
          { revalidate: false },
        )

        // Optimistically update the detail cache (if it's already loaded)
        await mutate(
          `/views/patient/documents/${documentId}?subjectId=${subjectId}`,
          (current?: PatientViewDocument) => {
            if (!current) return current
            return patchDocumentAsRead(current, scope)
          },
          { revalidate: false },
        )

        await axios.post(
          `/views/patient/documents/${documentId}/mark-as-read?subjectId=${subjectId?.toString() ?? ''}`,
          scope == null ? undefined : { scope },
        )

        // Revalidate all document queries to refetch with updated counts,
        // without clearing cached data (prevents UI flicker/loading states).
        await mutate((key) => typeof key === 'string' && key.includes('/documents?subjectId='))
      } catch (error) {
        console.error('Failed to mark document as read:', error)

        // If something went wrong, revalidate to restore cache from server.
        await mutate((key) => typeof key === 'string' && key.includes('/documents?subjectId='))
      }
    },
    [axios, mutate, subjectId],
  )

  return { markAsRead }
}
